// ─────────────────────────────────────────────────────────────────────────────
// RexyGraphFilters — the filter panel.
//
// Dark, translucent, compact, and out of the way: the graph stays the primary
// object on screen and the panel never competes with it.
//
// NO FORCE-STRENGTH CONTROLS. The macro geography is deliberate — City Hall at
// the origin, boroughs on stable anchors — and a slider that lets the user
// randomise it would destroy the one property the map exists to have. Node scale
// and link opacity are here because they are VIEWING preferences; layout is not.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { RexyTrustTier } from './graphTypes';
import { INITIAL_REXY_DOMAIN_COLOURS, UNRESOLVED_DOMAIN_COLOUR, resolveDomainColour } from './graphPalette';

export interface GraphFilterState {
  kinds: { [group: string]: boolean };
  domains: { [slug: string]: boolean };
  trust: { [tier: string]: boolean };
  relations: { [kind: string]: boolean };
  crossDomainEdges: boolean;
  orphans: boolean;
  labels: boolean;
  nodeScale: number;
  edgeAlphaScale: number;
  levelOverride: number | null;
}

// `services` is split out from `surfaces` so LIVE mode can offer exactly the four
// groups its structure contains. Default state is unchanged (every group starts
// true), so the synthetic lab renders identically — only which chip hides a
// synthetic `service` node moves.
export const KIND_GROUPS = ['domains', 'agents', 'memory', 'files', 'systems', 'surfaces', 'services'] as const;

/** The groups LIVE G2.0 actually has nodes for. Memory, files and systems are
 *  deep topology and arrive at G2.1; offering dead toggles for them would imply
 *  the graph is hiding something it never loaded. */
// G2.1 adds the two groups the live graph now genuinely contains. `files` stays
// OUT: live mode has no file, document, thread or record and offering a toggle
// for nothing would imply the graph is hiding something it never had.
export const LIVE_KIND_GROUPS = ['domains', 'agents', 'os memory', 'domain memory', 'agent memory',
  'databases', 'models', 'workflows', 'services', 'surfaces'] as const;

// TOOLS IS ABSENT ON PURPOSE. Rexy OS has no tool registry today, so live emits
// zero tool nodes; offering a toggle for them would imply tools exist and are
// merely hidden. The lab keeps its 'systems' toggle, which covers tools there.
export const TRUST_TIERS: RexyTrustTier[] = ['HIGHEST', 'HIGHER', 'HIGH', 'MEDIUM', 'LOW'];
export const RELATION_FILTERS = [
  'contains', 'reads', 'writes', 'routes', 'depends-on', 'references', 'approved-cross-domain',
] as const;

export function defaultFilterState(domainSlugs: string[]): GraphFilterState {
  // BOTH group vocabularies are seeded. Live uses finer group names than the lab,
  // and an unseeded key reads `undefined` — falsy — which would silently hide
  // every live node behind a filter the owner never touched.
  const kinds: { [k: string]: boolean } = {};
  KIND_GROUPS.forEach((g) => { kinds[g] = true; });
  LIVE_KIND_GROUPS.forEach((g) => { kinds[g] = true; });
  const domains: { [k: string]: boolean } = {};
  domainSlugs.forEach((d) => { domains[d] = true; });
  const trust: { [k: string]: boolean } = {};
  TRUST_TIERS.forEach((t) => { trust[t] = true; });
  const relations: { [k: string]: boolean } = {};
  RELATION_FILTERS.forEach((r) => { relations[r] = true; });
  return {
    kinds, domains, trust, relations,
    crossDomainEdges: true, orphans: true, labels: true,
    nodeScale: 1, edgeAlphaScale: 1, levelOverride: null,
  };
}

export interface RexyGraphFiltersProps {
  state: GraphFilterState;
  domainSlugs: string[];
  onChange: (next: GraphFilterState) => void;
  devMode: boolean;
  /** Which kind groups to offer. Defaults to all — LIVE mode passes the four it has. */
  kindGroups?: readonly string[];
  /** Trust is a deep-memory property; LIVE G2.0 carries none and hides the section
   *  rather than showing five tiers that filter nothing. */
  showTrust?: boolean;
}

export default function RexyGraphFilters({
  state, domainSlugs, onChange, devMode, kindGroups, showTrust,
}: RexyGraphFiltersProps): React.ReactElement {
  const groups: readonly string[] = kindGroups || KIND_GROUPS;
  const trustVisible = showTrust !== false;
  const toggle = (group: keyof GraphFilterState, key: string) => {
    const bag = { ...(state[group] as { [k: string]: boolean }) };
    bag[key] = !bag[key];
    onChange({ ...state, [group]: bag });
  };

  return (
    <div style={panelStyle}>
      <Section title="show">
        <div style={rowWrap}>
          {groups.map((g) => (
            <Toggle key={g} label={g} on={state.kinds[g]} onClick={() => toggle('kinds', g)} />
          ))}
          <Toggle label="cross-domain" on={state.crossDomainEdges}
            onClick={() => onChange({ ...state, crossDomainEdges: !state.crossDomainEdges })} />
          <Toggle label="orphans" on={state.orphans}
            onClick={() => onChange({ ...state, orphans: !state.orphans })} />
          <Toggle label="labels" on={state.labels}
            onClick={() => onChange({ ...state, labels: !state.labels })} />
        </div>
      </Section>

      <Section title="domains">
        <div style={rowWrap}>
          {domainSlugs.map((d) => {
            const resolved = resolveDomainColour(d);
            const known = Object.prototype.hasOwnProperty.call(INITIAL_REXY_DOMAIN_COLOURS, d);
            return (
              <button key={d} type="button" onClick={() => toggle('domains', d)} style={{
                ...chipStyle,
                opacity: state.domains[d] ? 1 : 0.34,
                borderColor: state.domains[d] ? resolved.hex : 'rgba(120,160,200,0.18)',
              }}>
                <span style={{ ...dotStyle, background: resolved.hex }} />
                {d}
                {!known && <span style={placeholderTag} title="no ruled colour — placeholder">?</span>}
              </button>
            );
          })}
        </div>
      </Section>

      {trustVisible && (
        <Section title="trust">
          <div style={rowWrap}>
            {TRUST_TIERS.map((t) => (
              <Toggle key={t} label={t} on={state.trust[t]} onClick={() => toggle('trust', t)} />
            ))}
          </div>
        </Section>
      )}

      <Section title="relations">
        <div style={rowWrap}>
          {RELATION_FILTERS.map((r) => (
            <Toggle key={r} label={r} on={state.relations[r]} onClick={() => toggle('relations', r)} />
          ))}
        </div>
      </Section>

      <Section title="view">
        <Slider label="node scale" value={state.nodeScale} min={0.5} max={2.5} step={0.1}
          onChange={(v) => onChange({ ...state, nodeScale: v })} />
        <Slider label="link opacity" value={state.edgeAlphaScale} min={0} max={4} step={0.1}
          onChange={(v) => onChange({ ...state, edgeAlphaScale: v })} />
      </Section>

      {devMode && (
        <Section title="dev">
          <div style={rowWrap}>
            {[null, 0, 1, 2, 3, 4].map((lv) => (
              <Toggle
                key={String(lv)}
                label={lv === null ? 'auto' : 'L' + lv}
                on={state.levelOverride === lv}
                onClick={() => onChange({ ...state, levelOverride: lv })}
              />
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={sectionTitleStyle}>{title}</div>
      {children}
    </div>
  );
}

function Toggle({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={{
      ...chipStyle,
      opacity: on ? 1 : 0.34,
      borderColor: on ? 'rgba(120,190,255,0.45)' : 'rgba(120,160,200,0.18)',
      color: on ? '#cfe6ff' : '#7286a0',
    }}>
      {label}
    </button>
  );
}

function Slider({ label, value, min, max, step, onChange }: {
  label: string; value: number; min: number; max: number; step: number;
  onChange: (v: number) => void;
}) {
  return (
    <label style={sliderRowStyle}>
      <span style={{ color: '#7286a0' }}>{label}</span>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ flex: 1, accentColor: '#2EA8FF' }} />
      <span style={{ color: '#cfe6ff', width: 30, textAlign: 'right' }}>{value.toFixed(1)}</span>
    </label>
  );
}

const panelStyle: React.CSSProperties = {
  pointerEvents: 'auto',
  width: 264,
  padding: '12px 13px',
  background: 'rgba(4,10,22,0.72)',
  border: '1px solid rgba(120,180,255,0.16)',
  borderRadius: 4,
  backdropFilter: 'blur(8px)',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 10.5,
  maxHeight: '78vh',
  overflowY: 'auto',
};

const sectionTitleStyle: React.CSSProperties = {
  color: '#4e6076', letterSpacing: 1.6, textTransform: 'uppercase',
  fontSize: 9, marginBottom: 6,
};

const rowWrap: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 4 };

const chipStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '3px 7px', background: 'transparent',
  border: '1px solid', borderRadius: 2, cursor: 'pointer',
  color: '#cfe6ff', fontFamily: 'inherit', fontSize: 9.5, letterSpacing: 0.6,
};

const dotStyle: React.CSSProperties = { width: 7, height: 7, borderRadius: 4, display: 'inline-block' };

const placeholderTag: React.CSSProperties = { color: UNRESOLVED_DOMAIN_COLOUR, marginLeft: 2 };

const sliderRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4,
};
